import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, TrendingDown, Loader2, Bug,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";

const WINDOW_OPTIONS = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d",  label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const KPI = ({ label, value, hint, tone = "amber" }) => (
  <div className="rounded-lg border border-white/5 bg-[#121214] p-4">
    <div className="font-mono-tech text-[9px] text-zinc-500 uppercase tracking-wider mb-2">{label}</div>
    <div className={`font-heading text-3xl font-bold tracking-tight ${tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-amber-400"}`}>
      {value}
    </div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </div>
);

const SectionTitle = ({ children, icon: Icon }) => (
  <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase text-zinc-500 tracking-wider mb-3">
    {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
    {children}
  </div>
);

export default function ReliabilityTab() {
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState("7d");
  const [tier, setTier] = useState("all");
  const [quality, setQuality] = useState("all");
  const [providerId, setProviderId] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { window };
      if (tier !== "all") params.tier = tier;
      if (quality !== "all") params.quality = quality;
      if (providerId !== "all") params.provider_id = providerId;
      const [dash, reps] = await Promise.all([
        api.get("/admin/reliability", { params }),
        api.get("/admin/reports", { params: { limit: 25 } }),
      ]);
      setData(dash.data);
      setReports(reps.data);
    } catch (e) {
      toast.error("Could not load reliability data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [window, tier, quality, providerId]);

  if (loading || !data) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-500" data-testid="reliability-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading reliability data…
      </div>
    );
  }

  const totals = data.totals || {};
  const retry = data.retry || {};
  const providerHealth = data.provider_health || [];
  const muted = data.muted_providers || [];

  const providerOptions = providerHealth.map((p) => p.provider_id);

  return (
    <div className="space-y-8" data-testid="reliability-tab">
      {/* Header + filters */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono-tech text-[10px] text-amber-400 mb-2">
            <Activity className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.5} />
            SYSTEM · RELIABILITY
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tighter">
            AI engine health
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Every generation is logged. Unstable engines auto-mute so users never see a broken experience.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger data-testid="filter-window" className="w-40 bg-[#121214] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1e] border-white/10">
              {WINDOW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger data-testid="filter-tier" className="w-36 bg-[#121214] border-white/10">
              <SelectValue placeholder="Tier" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1e] border-white/10">
              <SelectItem value="all">All tiers</SelectItem>
              {["free", "starter", "pro", "studio", "enterprise", "owner"].map((t) =>
                <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quality} onValueChange={setQuality}>
            <SelectTrigger data-testid="filter-quality" className="w-36 bg-[#121214] border-white/10">
              <SelectValue placeholder="Quality" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1e] border-white/10">
              <SelectItem value="all">All qualities</SelectItem>
              {["smart", "budget", "premium", "ultra", "preview", "manual"].map((q) =>
                <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
          {providerOptions.length > 0 && (
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger data-testid="filter-provider" className="w-44 bg-[#121214] border-white/10">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1e] border-white/10">
                <SelectItem value="all">All providers</SelectItem>
                {providerOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={load}
            variant="outline"
            data-testid="refresh-reliability"
            className="border-white/10 hover:bg-white/5"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="reliability-kpis">
        <KPI label="Attempts" value={totals.attempts ?? 0} hint={`${totals.completed ?? 0} completed`} />
        <KPI label="Success rate" value={`${totals.success_pct ?? 0}%`} tone={ (totals.success_pct ?? 0) >= 90 ? "green" : "amber" } />
        <KPI label="Retry recoveries" value={`${retry.success_pct ?? 0}%`} hint={`${retry.success ?? 0} / ${retry.attempts ?? 0}`} />
        <KPI label="Most unstable" value={data.most_unstable_provider || "—"} tone={data.most_unstable_provider ? "red" : "green"} />
      </div>

      {/* Currently muted providers banner */}
      {muted.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/[0.06] p-4" data-testid="muted-banner">
          <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase text-red-300 tracking-wider mb-2">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.75} /> Temporarily muted engines
          </div>
          <div className="flex flex-wrap gap-2">
            {muted.map((m) => (
              <Badge key={m.id} variant="outline" className="border-red-500/40 bg-red-500/10 text-red-200 font-mono-tech text-[10px]">
                {m.id} · until {(m.temporarily_disabled_until || "").slice(11, 16)} UTC
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Provider reliability radial */}
      <div className="rounded-lg border border-white/5 bg-[#121214] p-5">
        <SectionTitle icon={CheckCircle2}>Provider reliability score</SectionTitle>
        {providerHealth.length === 0 ? (
          <div className="text-sm text-zinc-500 py-10 text-center">No generation events yet in this window.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="provider-health-grid">
            {providerHealth.map((p) => (
              <div key={p.provider_id} className="rounded-md border border-white/5 bg-white/[0.02] p-4">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="font-mono-tech text-[10px] text-zinc-400 uppercase tracking-wider truncate">
                    {p.provider_id}
                  </div>
                  <div className={`font-heading text-2xl font-bold ${p.reliability_pct >= 95 ? "text-emerald-400" : p.reliability_pct >= 80 ? "text-amber-400" : "text-red-400"}`}>
                    {p.reliability_pct}%
                  </div>
                </div>
                <div className="h-16 min-h-[64px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart innerRadius="70%" outerRadius="100%" barSize={8} data={[{ value: p.reliability_pct, fill: p.reliability_pct >= 95 ? "#10b981" : p.reliability_pct >= 80 ? "#F5A524" : "#ef4444" }]} startAngle={180} endAngle={-180}>
                      <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                      <RadialBar background={{ fill: "rgba(255,255,255,0.05)" }} dataKey="value" cornerRadius={4} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px] font-mono-tech text-zinc-500 mt-2">
                  <div><div className="text-zinc-300">{p.attempts}</div><div>attempts</div></div>
                  <div><div className="text-red-300">{p.failures}</div><div>failures</div></div>
                  <div><div className="text-zinc-300">{Math.round((p.avg_render_ms || 0) / 100) / 10}s</div><div>avg render</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top error codes" data={data.top_error_codes} testid="chart-error-codes" />
        <ChartCard title="Errors by provider" data={data.errors_by_provider} testid="chart-errors-by-provider" />
        <ChartCard title="Errors by tier" data={data.errors_by_tier} testid="chart-errors-by-tier" />
        <ChartCard title="Errors by quality" data={data.errors_by_quality} testid="chart-errors-by-quality" />
      </div>

      {/* Problem reports */}
      <div className="rounded-lg border border-white/5 bg-[#121214] p-5">
        <SectionTitle icon={Bug}>Recent problem reports</SectionTitle>
        {reports.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No reports yet.</div>
        ) : (
          <div className="divide-y divide-white/5" data-testid="reports-list">
            {reports.map((r) => (
              <div key={r.id} className="py-3 flex flex-col md:flex-row md:items-center gap-2" data-testid={`report-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{r.description || "(no description)"}</div>
                  <div className="text-[11px] font-mono-tech text-zinc-500 mt-1">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.user_email || r.user_id} · {r.error_code || "no-code"} · {r.provider_id || "unknown"} · {r.tier}
                  </div>
                </div>
                <ReportStatusPicker report={r} onChange={load} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, data, testid }) {
  const rows = (data || []).slice(0, 8).map((d) => ({ name: d.key, count: d.count }));
  return (
    <div className="rounded-lg border border-white/5 bg-[#121214] p-5" data-testid={testid}>
      <SectionTitle icon={TrendingDown}>{title}</SectionTitle>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">No data.</div>
      ) : (
        <div className="h-60 min-h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10 }} width={30} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#1a1a1e", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                cursor={{ fill: "rgba(245,165,36,0.08)" }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {rows.map((_, i) => <Cell key={i} fill="#F5A524" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function ReportStatusPicker({ report, onChange }) {
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/reports/${report.id}`, { status });
      onChange?.();
    } catch { toast.error("Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <Select value={report.status || "open"} onValueChange={set} disabled={busy}>
      <SelectTrigger data-testid={`report-status-${report.id}`} className="w-40 bg-transparent border-white/10 h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#1a1a1e] border-white/10">
        <SelectItem value="open">Open</SelectItem>
        <SelectItem value="acknowledged">Acknowledged</SelectItem>
        <SelectItem value="resolved">Resolved</SelectItem>
      </SelectContent>
    </Select>
  );
}
